import _toConsumableArray from "/Users/vitalyrtishchev/code/mantine-master/docs/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _classCallCheck from "/Users/vitalyrtishchev/code/mantine-master/docs/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/Users/vitalyrtishchev/code/mantine-master/docs/node_modules/@babel/runtime/helpers/esm/createClass.js";

/**
 * @typedef {import('hast').Text} Text
 * @typedef {import('highlight.js').HighlightResult} HighlightResult
 * @typedef {import('highlight.js').HLJSOptions} HighlightOptions
 * @typedef {import('highlight.js').LanguageFn} HighlightSyntax
 * @typedef {import('highlight.js').Emitter} HighlightEmitter
 *
 * @typedef {{type: 'element', tagName: 'span', properties: {className: Array<string>}, children: Array<Span|Text>}} Span
 * @typedef {{type: 'root', data: {language: string|null, relevance: number}, children: Array<Span|Text>}} Root
 *
 * @typedef {Object} ExtraOptions
 * @property {Array<string>} [subset]
 *   List of allowed languages, defaults to all registered languages.
 *
 * @typedef {Object} Options
 *   Configuration.
 * @property {string} [prefix='hljs-']
 *   Class prefix.
 *
 * @typedef {Options & ExtraOptions} AutoOptions
 */
import high from 'highlight.js/lib/core';
import { fault } from 'fault';
var own = {}.hasOwnProperty;
var defaultPrefix = 'hljs-';
/**
 * Highlight `value` (code) as `language` (name).
 *
 * @param {string} language
 *   Programming language name.
 * @param {string} value
 *   Code to highlight.
 * @param {Options} [options={}]
 *   Configuration.
 * @returns {Root}
 *   A hast `Root` node.
 */

function highlight(language, value) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var prefix = options.prefix;

  if (typeof language !== 'string') {
    throw fault('Expected `string` for name, got `%s`', language);
  }

  if (!high.getLanguage(language)) {
    throw fault('Unknown language: `%s` is not registered', language);
  }

  if (typeof value !== 'string') {
    throw fault('Expected `string` for value, got `%s`', value);
  }

  if (prefix === null || prefix === undefined) {
    prefix = defaultPrefix;
  }

  high.configure({
    __emitter: HastEmitter,
    classPrefix: prefix
  });
  var result =
  /** @type {HighlightResult & {_emitter: HastEmitter}} */
  high.highlight(value, {
    language: language,
    ignoreIllegals: true
  });
  high.configure({}); // `highlight.js` seems to use this (currently) for broken grammars, so let’s
  // keep it in there just to be sure.

  /* c8 ignore next 3 */

  if (result.errorRaised) {
    throw result.errorRaised;
  } // @ts-expect-error: `language` is always defined in `highlight`.


  result._emitter.root.data.language = result.language;
  result._emitter.root.data.relevance = result.relevance;
  return result._emitter.root;
}
/**
 * Highlight `value` (code) and guess its programming language.
 *
 * @param {string} value
 *   Code to highlight.
 * @param {AutoOptions} [options={}]
 *   Configuration.
 * @returns {Root}
 *   A hast `Root` node.
 */


function highlightAuto(value) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var subset = options.subset || high.listLanguages();
  var prefix = options.prefix;
  var index = -1;
  /** @type {Root} */

  var result = {
    type: 'root',
    data: {
      language: null,
      relevance: 0
    },
    children: []
  };

  if (prefix === null || prefix === undefined) {
    prefix = defaultPrefix;
  }

  if (typeof value !== 'string') {
    throw fault('Expected `string` for value, got `%s`', value);
  }

  while (++index < subset.length) {
    var name = subset[index];
    if (!high.getLanguage(name)) continue;
    var current = highlight(name, value, options);
    if (current.data.relevance > result.data.relevance) result = current;
  }

  return result;
}
/**
 * Register a language.
 *
 * @param {string} language
 *   Programming language name.
 * @param {HighlightSyntax} syntax
 *   `highlight.js` language syntax.
 * @returns {void}
 */


function registerLanguage(language, syntax) {
  high.registerLanguage(language, syntax);
}
/**
 * Register aliases for already registered languages.
 *
 * @param {string|Record<string, string|Array<string>>} language
 *   Programming language name or a map of `language`s to `alias`es or `list`s
 * @param {string|Array<string>} [alias]
 *   New aliases for the programming language.
 * @returns {void}
 */


var registerAlias =
/**
 * @type {(
 *   ((language: string, alias: string|Array<string>) => void) &
 *   ((aliases: Record<string, string|Array<string>>) => void)
 * )}
 */

/**
 * @param {string|Record<string, string|Array<string>>} language
 * @param {string|Array<string>} [alias]
 * @returns {void}
 */
function registerAlias(language, alias) {
  if (typeof language === 'string') {
    // @ts-expect-error: should be a string in this overload.
    high.registerAliases(alias, {
      languageName: language
    });
  } else {
    /** @type {string} */
    var key;

    for (key in language) {
      if (own.call(language, key)) {
        high.registerAliases(language[key], {
          languageName: key
        });
      }
    }
  }
};
/**
 * Check whether an `alias` or `language` is registered.
 *
 * @param {string} aliasOrLanguage
 *   Name of a registered language or alias.
 * @returns {boolean}
 *   Whether `aliasOrlanguage` is registered.
 */


function registered(aliasOrLanguage) {
  return Boolean(high.getLanguage(aliasOrLanguage));
}
/**
 * List registered languages.
 *
 * @returns {Array<string>}
 *   Names of registered language.
 */


function listLanguages() {
  return high.listLanguages();
}
/** @type {HighlightEmitter} */


var HastEmitter = /*#__PURE__*/function () {
  /**
   * @param {HighlightOptions} options
   */
  function HastEmitter(options) {
    _classCallCheck(this, HastEmitter);

    /** @type {HighlightOptions} */
    this.options = options;
    /** @type {Root} */

    this.root = {
      type: 'root',
      data: {
        language: null,
        relevance: 0
      },
      children: []
    };
    /** @type {[Root, ...Array<Span>]} */

    this.stack = [this.root];
  }
  /**
   * @param {string} value
   */


  _createClass(HastEmitter, [{
    key: "addText",
    value: function addText(value) {
      if (value === '') return;
      var current = this.stack[this.stack.length - 1];
      var tail = current.children[current.children.length - 1];

      if (tail && tail.type === 'text') {
        tail.value += value;
      } else {
        current.children.push({
          type: 'text',
          value: value
        });
      }
    }
    /**
     * @param {string} value
     * @param {string} name
     */

  }, {
    key: "addKeyword",
    value: function addKeyword(value, name) {
      this.openNode(name);
      this.addText(value);
      this.closeNode();
    }
    /**
     * @param {HastEmitter} other
     * @param {string} name
     */

  }, {
    key: "addSublanguage",
    value: function addSublanguage(other, name) {
      var current = this.stack[this.stack.length - 1];
      var results = other.root.children;

      if (name) {
        current.children.push({
          type: 'element',
          tagName: 'span',
          properties: {
            className: [name]
          },
          children: results
        });
      } else {
        var _current$children;

        (_current$children = current.children).push.apply(_current$children, _toConsumableArray(results));
      }
    }
    /**
     * @param {string} name
     */

  }, {
    key: "openNode",
    value: function openNode(name) {
      var _this = this;

      // First “class” gets the prefix. Rest gets a repeated underscore suffix.
      // See: <https://github.com/highlightjs/highlight.js/commit/51806aa>
      // See: <https://github.com/wooorm/lowlight/issues/43>
      var className = name.split('.').map(function (d, i) {
        return i ? d + '_'.repeat(i) : _this.options.classPrefix + d;
      });
      var current = this.stack[this.stack.length - 1];
      /** @type {Span} */

      var child = {
        type: 'element',
        tagName: 'span',
        properties: {
          className: className
        },
        children: []
      };
      current.children.push(child);
      this.stack.push(child);
    }
    /**
     */

  }, {
    key: "closeNode",
    value: function closeNode() {
      this.stack.pop();
    }
    /**
     */

  }, {
    key: "closeAllNodes",
    value: function closeAllNodes() {}
    /**
     */

  }, {
    key: "finalize",
    value: function finalize() {}
    /**
     */

  }, {
    key: "toHTML",
    value: function toHTML() {
      return '';
    }
  }]);

  return HastEmitter;
}();

export var lowlight = {
  highlight: highlight,
  highlightAuto: highlightAuto,
  registerLanguage: registerLanguage,
  registered: registered,
  listLanguages: listLanguages,
  registerAlias: registerAlias
};